import { useEffect, useState, type ReactNode } from 'react'

import type { QuantMarketTone as QuantMarketStatus } from '@/ui/QuantMarketToolbar'

export interface QuantViewNoticeProps {
  status: QuantMarketStatus
  /** The coded failure copy (`viewFailureMessage`), or `null`. */
  error: string | null
  /** A single user-initiated reopen is offered. */
  canReconnect: boolean
  /** The user cancelled the open; `onReconnect` opens it again. */
  cancelled: boolean
  catalogChanged: boolean
  /** A refused later indicator list; the view stays open. */
  indicatorRefusal: string | null
  /** The last `status` frame's detail (e.g. `warming up`). */
  detail: string | null
  onReconnect: () => void
  onCancel: () => void
  /** True when no candle has arrived yet (the notice is then the whole canvas message). */
  empty: boolean
}

const STATE_COPY: Partial<Record<QuantMarketStatus, string>> = {
  closed: 'Market closed. The chart keeps the last candles.',
  no_feed: 'No feed. The chart keeps the last candles and recovers on its own.',
  reconnecting: 'Reconnecting. Missed candles fill in when the feed is back.',
}

function WarmupElapsed() {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setSeconds((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const m = Math.floor(seconds / 60)
  return (
    <span data-warmup-elapsed className="tabular-nums">
      {m > 0 ? `${m}m ${String(seconds % 60).padStart(2, '0')}s` : `${seconds}s`}
    </span>
  )
}

const BUTTON =
  'pointer-events-auto cursor-pointer border border-paper-35 bg-ink px-3 py-1.5 font-sans text-[10px] font-semibold tracking-[0.18em] text-paper uppercase'

/**
 * What a live view says over the chart (spec 070 D12): warming up with elapsed time and a Cancel (the
 * open can take minutes: no timeout, no error), a kept-chart state line for closed / no feed / reconnecting,
 * a plain coded failure with an optional single Reconnect, and the catalog-changed notice. Props only.
 */
export function QuantViewNotice({ status, error, canReconnect, cancelled, catalogChanged, indicatorRefusal, detail, onReconnect, onCancel, empty }: QuantViewNoticeProps) {
  const warming = status === 'warming_up' || (status === 'connecting' && empty)
  const lines: ReactNode[] = []
  if (cancelled) {
    lines.push(
      <div key="cancelled" className="flex items-center gap-3">
        <span>Opening cancelled.</span>
        <button type="button" aria-label="Open the live view again" onClick={onReconnect} className={BUTTON}>
          Open again
        </button>
      </div>,
    )
  } else if (status === 'failed') {
    lines.push(
      <div key="failed" role="alert" className="flex items-center gap-3 text-alert">
        <span>{error ?? 'The live view failed.'}</span>
        {canReconnect && (
          <button type="button" aria-label="Reconnect live view" onClick={onReconnect} className={BUTTON}>
            Reconnect
          </button>
        )}
      </div>,
    )
  } else if (warming) {
    lines.push(
      <div key="warm" role="status" data-view-warming className="flex items-center gap-3">
        <span>Warming up{detail !== null && detail !== 'warming up' ? ` · ${detail}` : ''} · </span>
        <WarmupElapsed />
        <button type="button" aria-label="Cancel opening the live view" onClick={onCancel} className={BUTTON}>
          Cancel
        </button>
      </div>,
    )
  } else {
    const text = STATE_COPY[status]
    if (text !== undefined) lines.push(<div key="state" role="status" data-view-state={status}>{text}</div>)
  }
  if (catalogChanged) lines.push(<div key="cat" data-catalog-changed>The indicator catalog changed. Some indicators may differ from what this page lists.</div>)
  if (indicatorRefusal !== null) lines.push(<div key="ref" role="alert" className="text-alert">{indicatorRefusal}</div>)
  if (lines.length === 0) return null
  return (
    <div
      data-quant-view-notice
      className={
        empty
          ? 'pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 font-mono text-[11px] text-paper-50'
          : 'pointer-events-none absolute inset-x-0 top-2 z-10 flex flex-col items-center gap-1 font-mono text-[10px] text-paper-50'
      }
    >
      {lines}
    </div>
  )
}
